import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

import type { AnimatorPreviewGeometry } from '../../engine/prism.js';
import { bounds, cameraMatrix, frameBounds, projectPoint, type OrbitCamera, type Vec3 } from './preview-model.js';

/** WebGL 2 shaded mesh of the animator preview (positions are re-uploaded per frame). */
class PreviewRenderer {
  gl: WebGL2RenderingContext;
  program: WebGLProgram;
  grid: { vao: WebGLVertexArrayObject; buffer: WebGLBuffer; count: number };
  uniforms: { camera: WebGLUniformLocation | null; color: WebGLUniformLocation | null; flat: WebGLUniformLocation | null };
  meshes: { vao: WebGLVertexArrayObject; vertex: WebGLBuffer; indices: WebGLBuffer; count: number; offset: number; vertices: number; color: number[] }[] = [];

  constructor(canvas: HTMLCanvasElement) {
    const gl = canvas.getContext('webgl2', { antialias: true, alpha: false });
    if (!gl) throw new Error('WebGL 2 is needed for the animator preview.');
    this.gl = gl;
    const shader = (type: number, source: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, source);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'Shader error');
      return s;
    };
    const vs = shader(
      gl.VERTEX_SHADER,
      `#version 300 es
      layout(location=0) in vec3 position;uniform mat4 camera;out vec3 world;
      void main(){world=position;gl_Position=camera*vec4(position,1.);}`,
    );
    const fs = shader(
      gl.FRAGMENT_SHADER,
      `#version 300 es
      precision highp float;in vec3 world;uniform vec4 color;uniform bool flat_;out vec4 outColor;
      void main(){if(flat_){outColor=color;return;}vec3 n=normalize(cross(dFdx(world),dFdy(world)));
      float key=max(dot(n,normalize(vec3(.5,.8,.6))),0.);float fill=max(dot(n,normalize(vec3(-.6,.3,-.5))),0.)*.35;
      outColor=vec4(color.rgb*(.22+.78*key+fill),1.);}`,
    );
    this.program = gl.createProgram()!;
    gl.attachShader(this.program, vs);
    gl.attachShader(this.program, fs);
    gl.linkProgram(this.program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(this.program, gl.LINK_STATUS)) throw new Error('Could not initialize the animator preview.');
    this.uniforms = {
      camera: gl.getUniformLocation(this.program, 'camera'),
      color: gl.getUniformLocation(this.program, 'color'),
      flat: gl.getUniformLocation(this.program, 'flat_'),
    };
    const vao = gl.createVertexArray()!;
    const buffer = gl.createBuffer()!;
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 12, 0);
    gl.bindVertexArray(null);
    this.grid = { vao, buffer, count: 0 };
  }

  load(geometry: AnimatorPreviewGeometry) {
    this.clear();
    const gl = this.gl;
    let offset = 0;
    this.meshes = geometry.meshes.map((m) => {
      const vao = gl.createVertexArray()!;
      const vertex = gl.createBuffer()!;
      const indices = gl.createBuffer()!;
      gl.bindVertexArray(vao);
      gl.bindBuffer(gl.ARRAY_BUFFER, vertex);
      gl.bufferData(gl.ARRAY_BUFFER, m.vertices * 12, gl.DYNAMIC_DRAW);
      gl.enableVertexAttribArray(0);
      gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 12, 0);
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indices);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(m.indices), gl.STATIC_DRAW);
      gl.bindVertexArray(null);
      const mesh = { vao, vertex, indices, count: m.indices.length, offset, vertices: m.vertices, color: m.color };
      offset += m.vertices * 3;
      return mesh;
    });
  }

  /** Ground grid on y = `floor`, `size` units across. */
  setGrid(floor: number, size: number, center: Vec3) {
    const gl = this.gl;
    const lines: number[] = [];
    const n = 10;
    const step = size / n;
    const cx = Math.round(center[0] / step) * step;
    const cz = Math.round(center[2] / step) * step;
    for (let i = -n; i <= n; i++) {
      lines.push(cx + i * step, floor, cz - n * step, cx + i * step, floor, cz + n * step);
      lines.push(cx - n * step, floor, cz + i * step, cx + n * step, floor, cz + i * step);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, this.grid.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(lines), gl.STATIC_DRAW);
    this.grid.count = lines.length / 3;
  }

  draw(positions: Float32Array | null, camera: Float32Array) {
    const gl = this.gl;
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
    gl.clearColor(0.075, 0.082, 0.1, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.useProgram(this.program);
    gl.uniformMatrix4fv(this.uniforms.camera, false, camera);
    if (this.grid.count) {
      gl.uniform1i(this.uniforms.flat, 1);
      gl.uniform4f(this.uniforms.color, 0.2, 0.22, 0.27, 1);
      gl.bindVertexArray(this.grid.vao);
      gl.drawArrays(gl.LINES, 0, this.grid.count);
    }
    gl.uniform1i(this.uniforms.flat, 0);
    for (const m of this.meshes) {
      gl.bindVertexArray(m.vao);
      if (positions && positions.length >= m.offset + m.vertices * 3) {
        gl.bindBuffer(gl.ARRAY_BUFFER, m.vertex);
        gl.bufferSubData(gl.ARRAY_BUFFER, 0, positions, m.offset, m.vertices * 3);
      }
      gl.uniform4f(this.uniforms.color, m.color[0] ?? 0.65, m.color[1] ?? 0.68, m.color[2] ?? 0.74, 1);
      gl.drawElements(gl.TRIANGLES, m.count, gl.UNSIGNED_INT, 0);
    }
    gl.bindVertexArray(null);
  }

  clear() {
    const gl = this.gl;
    for (const m of this.meshes) {
      gl.deleteVertexArray(m.vao);
      gl.deleteBuffer(m.vertex);
      gl.deleteBuffer(m.indices);
    }
    this.meshes = [];
  }

  dispose() {
    this.clear();
    this.gl.deleteVertexArray(this.grid.vao);
    this.gl.deleteBuffer(this.grid.buffer);
    this.gl.deleteProgram(this.program);
  }
}

/**
 * Orbit-camera viewport of the animator preview: shaded skinned mesh, ground
 * grid and an optional joint overlay. Drag = orbit, Shift/middle/right drag =
 * pan, wheel = zoom, double-click = frame the model.
 */
export function PreviewViewport({
  geometry,
  positions,
  joints,
  showJoints,
  version,
}: {
  geometry: AnimatorPreviewGeometry | null;
  positions: Float32Array | null;
  joints: number[] | null;
  showJoints: boolean;
  /** Bumped whenever a new pose arrives (positions may be the same array). */
  version: number;
}) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const overlay = useRef<HTMLCanvasElement>(null);
  const renderer = useRef<PreviewRenderer | null>(null);
  const [size, setSize] = useState<[number, number]>([300, 220]);
  const [camera, setCamera] = useState<OrbitCamera>({ yaw: 0.5, pitch: 0.18, distance: 4, target: [0, 1, 0] });
  const [error, setError] = useState('');
  const drag = useRef<{ x: number; y: number; pan: boolean; camera: OrbitCamera } | null>(null);
  const framedFor = useRef<AnimatorPreviewGeometry | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize([Math.max(1, entry.contentRect.width), Math.max(1, entry.contentRect.height)]);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!canvas.current) return;
    try {
      renderer.current = new PreviewRenderer(canvas.current);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    return () => {
      renderer.current?.dispose();
      renderer.current = null;
    };
  }, []);

  const frame = () => {
    const box = positions && bounds(positions);
    if (!box) return;
    setCamera(frameBounds(box.min, box.max));
    const extent = Math.max(box.max[0] - box.min[0], box.max[1] - box.min[1], box.max[2] - box.min[2], 0.1);
    renderer.current?.setGrid(box.min[1], extent * 4, [(box.min[0] + box.max[0]) / 2, 0, (box.min[2] + box.max[2]) / 2]);
  };

  useEffect(() => {
    if (geometry) renderer.current?.load(geometry);
  }, [geometry]);

  useEffect(() => {
    // Frame each new model once its first pose arrives.
    if (geometry && positions && framedFor.current !== geometry) {
      framedFor.current = geometry;
      frame();
    }
  });

  useEffect(() => {
    const c = canvas.current;
    const r = renderer.current;
    if (!c || !r) return;
    const dpr = Math.min(devicePixelRatio, 2);
    const w = Math.round(size[0] * dpr);
    const h = Math.round(size[1] * dpr);
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    const { matrix } = cameraMatrix(camera, size[0] / size[1]);
    r.draw(positions, matrix);
    const o = overlay.current;
    const ctx = o?.getContext('2d');
    if (!o || !ctx) return;
    if (o.width !== w || o.height !== h) {
      o.width = w;
      o.height = h;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size[0], size[1]);
    if (!showJoints || !geometry || !joints) return;
    const pts = geometry.joints.map((_, i) => projectPoint(matrix, [joints[i * 3]!, joints[i * 3 + 1]!, joints[i * 3 + 2]!], size[0], size[1]));
    ctx.strokeStyle = 'rgba(120, 200, 255, 0.85)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    geometry.joints.forEach((j, i) => {
      const a = pts[i];
      const b = j.parent !== null ? pts[j.parent] : null;
      if (a && b) {
        ctx.moveTo(b[0], b[1]);
        ctx.lineTo(a[0], a[1]);
      }
    });
    ctx.stroke();
    ctx.fillStyle = 'rgba(160, 220, 255, 0.95)';
    for (const p of pts) {
      if (!p) continue;
      ctx.beginPath();
      ctx.arc(p[0], p[1], 2.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }, [positions, joints, camera, size, showJoints, geometry, version]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, pan: e.shiftKey || e.button === 1 || e.button === 2, camera };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    const c = d.camera;
    if (d.pan) {
      const k = (c.distance * 1.1) / size[1];
      const right: Vec3 = [Math.cos(c.yaw), 0, -Math.sin(c.yaw)];
      const up: Vec3 = [-Math.sin(c.pitch) * Math.sin(c.yaw), Math.cos(c.pitch), -Math.sin(c.pitch) * Math.cos(c.yaw)];
      setCamera({ ...c, target: c.target.map((v, i) => v - right[i]! * dx * k + up[i]! * dy * k) as Vec3 });
    } else {
      setCamera({ ...c, yaw: c.yaw - dx * 0.008, pitch: Math.max(-1.5, Math.min(1.5, c.pitch + dy * 0.008)) });
    }
  };

  return (
    <div
      ref={host}
      className="anim-preview-view"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
      onContextMenu={(e) => e.preventDefault()}
      onDoubleClick={frame}
      onWheel={(e) => setCamera((c) => ({ ...c, distance: Math.max(0.02, c.distance * Math.exp(e.deltaY * 0.0012)) }))}
      title="Drag to orbit · Shift/right-drag to pan · wheel to zoom · double-click to frame"
    >
      <canvas ref={canvas} />
      <canvas ref={overlay} className="anim-preview-overlay" />
      {error && <p className="anim-preview-msg">{error}</p>}
    </div>
  );
}
